import { Box, Card, CardContent, Container, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import WO2Button from 'src/common/components/button/Button';
import { WO2ToggleSwitch } from '../../../../../common';
import DateRangePicker from '../../../../../common/components/DateRangePicker';
import { DateRange } from '../../../../../store/types/dateRange';
import { Props } from '../container';
import { ReportAttachmentType, ReportFormat } from '../store/types';
import { ReportingToolbar } from './ReportingToolbar';
import { UploadReportAttachment } from './UploadReportAttachment';
import { MergeReportsToggle } from './MergeReportsToggle';
import { ReportFormatOption } from './ReportFormatOption';
import { ReportingComponentList } from './ReportingComponentList';
import { ReportingComponentListOptions } from '../store/constants';

export const ReportsComponent = (props: Props): JSX.Element => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentReportType, setCurrentReportType] = useState<ReportAttachmentType>(ReportAttachmentType.None);
  const [fullReports, setFullReports] = useState<boolean>(false);

  const parameters = props.parameters;

  useEffect(() => {
    if (!parameters.selectedParameter) return;
    props.getReportParametersById({
      id: parameters.selectedParameter?.id,
    });
  }, [parameters.selectedParameter]);

  const updateReportDates = (dates: DateRange) => {
    props.setDateParameter(dates);
  };

  const closeUploader = () => {
    setIsMenuOpen(false);

    currentReportType === ReportAttachmentType.CoverLetter ? props.setIncludeCoverLetterParameter(false) : props.setIncludeAppendixParameter(false);
  };

  const setFile = (file: File) => {
    setIsMenuOpen(false);

    if (currentReportType === ReportAttachmentType.CoverLetter) {
      props.saveCoverPageFile(file);
    } else if (currentReportType === ReportAttachmentType.Appendix) {
      props.saveAppendixFile(file);
    }
  };

  useEffect(() => {
    const clientId = Number(props.match.params.id) ?? 0;
    if (clientId > 0) {
      props.fetchClientAccount(clientId);
      props.getCGTRecordsForClient(clientId);

      if (props.afslId !== undefined && props.afslId > 0 && !props.hasClientDirectAccess) {
        props.fetchReportParametersList({ afslId: [props.afslId], kind: 'C' });
      }
    }
  }, [props.fetchClientAccount, props.getCGTRecordsForClient, props.match.params.id, props.afslId]);

  useEffect(() => {
    if (props.afslId && props.afslId > 0) {
      props.fetchBenchmarks(props.afslId);
    }
  }, [props.afslId]);

  useEffect(() => {
    props.setUseTriumphAsDataSourceParameter(!parameters.isDataFromTriumph);
  }, [props.isDataFromTriumph]);

  const handleClickIncludeCoverLetter = () => {
    props.setIncludeCoverLetterParameter(!parameters.includeCoverLetter);
    setIsMenuOpen(!parameters.includeCoverLetter);
    setCurrentReportType(ReportAttachmentType.CoverLetter);
  };

  const handleClickMergeReports = () => {
    props.setMergeReportsParameter(!parameters.mergeReports);
  };

  const handleClickIncludeAppendix = () => {
    props.setIncludeAppendixParameter(!parameters.includeAppendix);
    setIsMenuOpen(!parameters.includeAppendix);
    setCurrentReportType(ReportAttachmentType.Appendix);
  };

  const handleClickIncludeLogo = () => {
    props.setIncludeLogoParameter(!parameters.includeLogo);
  };

  return (
    <Container style={{ paddingTop: '20px' }}>
      <ReportingToolbar
        savedParametersList={props.savedParametersList}
        reportGeneration={props.reportGeneration}
        generateProgress={props.generateProgress}
        parameters={parameters}
        setSelectedReportingParameter={props.setSelectedReportingParameter}
        setReportingParameter={props.setReportingParameter}
        setShowItemOderingModalParameter={props.setShowItemOderingModalParameter}
        setSelectedReportOrderParameter={props.setSelectedReportOrderParameter}
        setReportGeneration={props.setReportGeneration}
        isDataFromTriumph={props.isDataFromTriumph}
        handleClickMergeReports={handleClickMergeReports}
        initiateReportGeneration={props.initiateReportGeneration}
        clientId={props.match.params.id}
        hasClientDirectAccess={props.hasClientDirectAccess}
      />
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Card elevation={0}>
            <CardContent>
              <Typography mb={2} variant="h3">
                Report Parameters
              </Typography>
              <DateRangePicker dateRange={parameters?.dates} setDateRange={updateReportDates} inceptionDate={parameters.inceptionDate} />
              <Box mt="15px">
                <MergeReportsToggle
                  mergeReports={parameters.mergeReports}
                  setMergeReportsParameter={props.setMergeReportsParameter}
                  setShowItemOderingModalParameter={props.setShowItemOderingModalParameter}
                  disabled={parameters.reportsToSort.length < 2 || parameters.reportFormat === ReportFormat.JSON}
                />
                <Grid container style={{ paddingTop: '10px' }}>
                  <WO2ToggleSwitch
                    checked={parameters.includeLogo}
                    onChange={handleClickIncludeLogo}
                    color="primary"
                    name="includeLogo"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                  />
                  <Typography variant="h4" style={{ paddingTop: '10px' }}>
                    Include Logo
                  </Typography>
                </Grid>
                <Grid container style={{ paddingTop: '10px' }}>
                  <WO2ToggleSwitch
                    checked={parameters.includeCoverLetter}
                    onChange={handleClickIncludeCoverLetter}
                    color="primary"
                    name="includeCoverPage"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                    disabled={parameters.reportFormat == ReportFormat.Excel || parameters.reportFormat == ReportFormat.JSON}
                  />
                  <Typography variant="h4" style={{ paddingTop: '10px' }}>
                    Cover Page
                  </Typography>
                </Grid>
                <Grid container style={{ paddingTop: '10px' }}>
                  <WO2ToggleSwitch
                    checked={parameters.includeAppendix}
                    onChange={handleClickIncludeAppendix}
                    color="primary"
                    name="includeAppendix"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                    disabled={parameters.reportFormat == ReportFormat.Excel || parameters.reportFormat == ReportFormat.JSON}
                  />
                  <Typography variant="h4" style={{ paddingTop: '10px' }}>
                    Appendix
                  </Typography>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item container display="flex" direction="column" xs={9} mt={2} spacing={1}>
          <Grid item container spacing={2} alignItems="center">
            <Grid item>
              <Box gap={2} display="flex" alignItems="center">
                <WO2Button variant="contained" onClick={props.expandAll}>
                  Expand
                </WO2Button>
                <ReportFormatOption
                  format={parameters?.reportFormat}
                  setReportFormat={props.setReportFormat}
                  hasClientEditAdminOnlyPermission={props.hasClientEditAdminOnlyPermission}
                  isDisplayOnly={false}
                />
                {props.hasClientEditAdminOnlyPermission && (
                  <Box display="flex" alignItems="center">
                    <WO2ToggleSwitch checked={fullReports} onChange={(e) => setFullReports(e.target.checked)} color="primary" />
                    <Typography variant="h4">Full Reports</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          <ReportingComponentList {...props} reportingList={ReportingComponentListOptions} fullReports={fullReports} />
        </Grid>
      </Grid>
      <UploadReportAttachment isOpen={isMenuOpen} handleImport={setFile} handleClose={closeUploader} title={`Upload ${currentReportType}`} />
    </Container>
  );
};
